import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { BookOpen, Code2, BarChart3, Users, CalendarDays, Megaphone } from 'lucide-react';

import { BatchHubHeader } from './hub/BatchHubHeader';
import { QuickAccessTile } from './hub/QuickAccessTile';
import { CommunityCta } from './hub/CommunityCta';
import { ComingSoonCallout } from './hub/ComingSoonCallout';
import { BatchNextActionCard } from './hub/BatchNextActionCard';
import { BatchContinueCard, type ContinueTarget } from './hub/BatchContinueCard';
import { BatchTodayCard } from './hub/BatchTodayCard';
import { BatchProgressSnapshot } from './hub/BatchProgressSnapshot';
import { BatchAnnouncementCard } from './hub/BatchAnnouncementCard';
import { pickResumeTarget } from './resumeTarget';

import { PlaceholderCard } from '@/features/Dashboard/home/PlaceholderCard';
import { useHome } from '@/lib/hooks/useHome';
import { useBatchCurriculum, useLeaderboard } from '@/lib/hooks/useLms';

export function BatchHub({ batchId }: { batchId: string }) {
  const [showCommunityCallout, setShowCommunityCallout] = useState(false);
  const { data: home, isLoading, isError, refetch } = useHome(batchId);
  const { data: leaderboard } = useLeaderboard(batchId);
  const { data: curriculum } = useBatchCurriculum(batchId);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
      </div>
    );
  }

  if (isError || !home) {
    return (
      <div className="mx-auto max-w-[1150px] p-4 sm:p-6">
        <div className="rounded-[20px] border border-[#ececec] bg-white p-6 text-center shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]">
          <p className="text-[#9ca3af]">Couldn&apos;t load this batch.</p>
          <button onClick={() => refetch()} className="mt-3 rounded-[14px] bg-[#ff5a1f] px-4 py-2 text-sm font-semibold text-white">Retry</button>
        </div>
      </div>
    );
  }

  const batchName = home.batch?.name ?? 'Batch';
  const studentCount = leaderboard?.batch?.total_students ?? 0;
  const announcements = home.announcements ?? [];

  let continueTarget: ContinueTarget | null = null;
  if (curriculum) {
    const resume = pickResumeTarget(curriculum.modules ?? []);
    const module = resume && curriculum.modules.find((m) => m.id === resume.moduleId);
    const topic = module && module.topics.find((t) => t.id === resume.topicId);
    if (module && topic) {
      continueTarget = {
        topicId: topic.id,
        topicTitle: topic.title,
        moduleTitle: module.title,
        pct: curriculum.total_topics > 0
          ? Math.round((curriculum.completed_topics / curriculum.total_topics) * 100)
          : 0,
      };
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-8 px-4 py-6 sm:px-10 sm:py-8">
      <BatchHubHeader batchName={batchName} studentCount={studentCount} />

      {home.next_session ? (
        <BatchNextActionCard session={home.next_session} batchId={batchId} />
      ) : continueTarget ? (
        <BatchContinueCard batchId={batchId} target={continueTarget} />
      ) : (
        <PlaceholderCard icon={CalendarDays} title="No upcoming class"
          description="You're all caught up on live sessions — check back later." />
      )}

      <section className="flex flex-col gap-4">
        <h2 className="text-base font-bold text-[#111827]">Quick Access</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link to="/courses/$batchId/learn" params={{ batchId }} className="block">
            <QuickAccessTile icon={BookOpen} iconBg="#fff5f0" iconColor="#ff5a1f" title="Learn" description="Explore topics, sessions and recordings" />
          </Link>
          <Link to="/courses/$batchId/practice" params={{ batchId }} className="block">
            <QuickAccessTile icon={Code2} iconBg="#f5f3ff" iconColor="#8b5cf6" title="Practice" description="Assignments, problems and projects" />
          </Link>
          <Link to="/progress" className="block">
            <QuickAccessTile icon={BarChart3} iconBg="#f5f3ff" iconColor="#8b5cf6" title="Progress" description="Track performance and milestones" />
          </Link>
          <button
            type="button"
            onClick={() => setShowCommunityCallout((s) => !s)}
            aria-expanded={showCommunityCallout}
            aria-label="Community"
            className="block w-full text-left"
          >
            <QuickAccessTile icon={Users} iconBg="#fdf2f8" iconColor="#ec4899" title="Community" description="Connect, discuss and collaborate" comingSoon />
          </button>
        </div>
        <ComingSoonCallout show={showCommunityCallout} onDismiss={() => setShowCommunityCallout(false)} />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <BatchTodayCard
          todayEvents={home.today_events ?? []}
          nextSession={home.next_session}
          dueItems={home.due_items ?? []}
        />
        <BatchProgressSnapshot
          coursePct={Math.round(home.stats.course_pct)}
          attendancePct={Math.round(home.readiness?.attendance_pct ?? 0)}
          streakDays={home.streak?.current ?? 0}
        />
        {announcements.length > 0 ? (
          <BatchAnnouncementCard announcements={announcements} />
        ) : (
          <PlaceholderCard icon={Megaphone} title="No announcements"
            description="Batch announcements will show up here." />
        )}
      </div>

      <CommunityCta />
    </div>
  );
}
